import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";

export default function FindPartners() {
  const [partners, setPartners] = useState(null);

  useEffect(() => {
    api
      .get("/public/partners")
      .then(({ data }) => setPartners(data.partners))
      .catch(() => setPartners([]));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 px-5 py-12" data-testid="find-partners-page">
      <div className="mx-auto max-w-4xl">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">
          ← Alabama Church School
        </Link>
        <h1 className="mt-4 font-display text-4xl font-bold text-slate-900 sm:text-5xl">
          Participating educational partners
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-600">
          If you do not yet have an educational partner, you can choose one below and enroll with
          Alabama Church School through their enrollment page.
        </p>

        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-slate-600" data-testid="directory-disclaimer">
          Each educational provider listed below operates independently from Alabama Church School.
          Alabama Church School does not create, provide, evaluate, approve, or endorse any
          partner's curriculum or instructional services. Families independently choose the
          educational provider and curriculum that best meets their needs.
        </p>

        {!partners ? (
          <Loader2 className="mt-10 h-5 w-5 animate-spin text-slate-400" />
        ) : partners.length === 0 ? (
          <p className="mt-10 text-sm text-slate-600" data-testid="no-partners">
            No partners are listed at the moment. Please use the enrollment link provided by your
            educational partner.
          </p>
        ) : (
          <div className="mt-10 space-y-4">
            {partners.map((p) => (
              <Card key={p.slug} className="border-slate-200">
                <CardContent className="flex flex-wrap items-center gap-5 p-6">
                  {p.logo_url ? (
                    <img
                      src={p.logo_url}
                      alt={p.name}
                      className="h-14 w-14 shrink-0 rounded-md object-contain"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-slate-100 font-display text-lg font-bold text-slate-500">
                      {p.name.slice(0, 1)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-base font-semibold text-slate-900">{p.name}</h2>
                    {p.description && (
                      <p className="mt-1 text-sm text-slate-600">{p.description}</p>
                    )}
                    {p.website_url && (
                      <a
                        href={p.website_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline"
                        data-testid={`partner-website-${p.slug}`}
                      >
                        Visit their website <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                  <Link to={`/${p.slug}`} className="shrink-0">
                    <Button className="gap-2" data-testid={`partner-enroll-${p.slug}`}>
                      Enroll with this partner <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
